import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, ChevronLeft, ChevronRight, Clock, CheckCircle,
  Circle, Save, X, Wrench, Bike, AlertTriangle, Bell, CalendarDays,
  Users, Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_NAMES_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const CATEGORIES = [
  { value: 'task', label: 'Tarea', icon: CheckCircle, color: 'text-blue-500' },
  { value: 'revision', label: 'Revisión', icon: Wrench, color: 'text-orange-500' },
  { value: 'delivery', label: 'Entrega', icon: Bike, color: 'text-green-500' },
  { value: 'urgent', label: 'Urgente', icon: AlertTriangle, color: 'text-red-500' },
  { value: 'reminder', label: 'Recordatorio', icon: Bell, color: 'text-purple-500' },
  { value: 'meeting', label: 'Reunión', icon: Users, color: 'text-sky-500' },
  { value: 'trip', label: 'Viaje', icon: Car, color: 'text-emerald-500' },
];

const REMINDERS = [
  { value: 'none', label: 'Sin recordatorio' },
  { value: '10min', label: '10 minutos antes' },
  { value: '30min', label: '30 minutos antes' },
  { value: '1hour', label: '1 hora antes' },
  { value: '2hours', label: '2 horas antes' },
  { value: '24hours', label: '24 horas antes' },
];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function formatDateStr(d) {
  return d.toISOString().split('T')[0];
}

function getCatInfo(cat) {
  return CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
}

export default function Calendar() {
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [weekData, setWeekData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(formatDateStr(new Date()));
  const [dayEvents, setDayEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', category: 'task', reminder: 'none' });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const notifInterval = useRef(null);

  const fetchWeek = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/week-summary`, { params: { start_date: formatDateStr(weekStart) } });
      setWeekData(res.data);
    } catch {}
  }, [weekStart]);

  const fetchDayEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/events`, { params: { date_from: selectedDate, date_to: selectedDate } });
      setDayEvents(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);
  useEffect(() => { fetchDayEvents(); }, [fetchDayEvents]);

  // Check for upcoming reminders every 30 seconds
  useEffect(() => {
    const checkReminders = async () => {
      try {
        const res = await axios.get(`${API}/calendar/events/upcoming`);
        if (res.data.length > 0) {
          setNotifications(res.data);
        }
      } catch {}
    };
    checkReminders();
    notifInterval.current = setInterval(checkReminders, 30000);
    return () => clearInterval(notifInterval.current);
  }, []);

  const dismissNotification = async (eventId) => {
    try {
      await axios.put(`${API}/calendar/events/${eventId}/dismiss-reminder`);
      setNotifications(prev => prev.filter(n => n.id !== eventId));
    } catch {}
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  const goToday = () => {
    setWeekStart(getMonday(new Date()));
    setSelectedDate(formatDateStr(new Date()));
  };

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', description: '', date: selectedDate, time: '09:00', category: 'task', reminder: 'none' });
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({ title: ev.title, description: ev.description || '', date: ev.date, time: ev.time, category: ev.category || 'task', reminder: ev.reminder || 'none' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('El título es obligatorio'); return; }
    if (!form.date) { toast.error('La fecha es obligatoria'); return; }
    if (!form.time) { toast.error('La hora es obligatoria'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/calendar/events/${editing.id}`, form);
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/calendar/events`, form);
        toast.success('Evento creado');
      }
      setDialogOpen(false);
      fetchWeek();
      fetchDayEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/calendar/events/${deleteId}`);
      toast.success('Evento eliminado');
      setDeleteId(null);
      fetchWeek();
      fetchDayEvents();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleToggleComplete = async (eventId) => {
    try {
      await axios.put(`${API}/calendar/events/${eventId}/complete`);
      fetchDayEvents();
      fetchWeek();
    } catch {}
  };

  const today = formatDateStr(new Date());

  // Month/year label
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const monthLabel = weekStart.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  // Selected day label
  const selDateObj = new Date(selectedDate + 'T00:00:00');
  const dayOfWeek = selDateObj.getDay();
  const dayIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const selectedDayLabel = `${DAY_NAMES_FULL[dayIdx]} ${selDateObj.getDate()}`;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="calendar-page">
      {/* Notification banners */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map(n => {
            const cat = getCatInfo(n.category);
            const CatIcon = cat.icon;
            return (
              <div key={n.id} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 animate-pulse" data-testid={`notification-${n.id}`}>
                <Bell size={18} className="text-amber-500 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800 flex items-center gap-1">
                    <CatIcon size={14} className={cat.color} /> {n.time} — {n.title}
                  </p>
                  {n.description && <p className="text-xs text-amber-600">{n.description}</p>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => dismissNotification(n.id)} data-testid="dismiss-notif-btn">
                  <X size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="page-title">Calendario</h1>
        <Button onClick={openNew} className="bg-teal-600 hover:bg-teal-700" data-testid="new-event-btn">
          <Plus size={16} className="mr-1" /> Nuevo Evento
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* LEFT: Week view */}
        <div className="space-y-3">
          {/* Week navigation */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-3">
                <Button size="sm" variant="ghost" onClick={prevWeek} data-testid="prev-week-btn"><ChevronLeft size={18} /></Button>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-800 capitalize">{monthLabel}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={nextWeek} data-testid="next-week-btn"><ChevronRight size={18} /></Button>
              </div>
              <div className="flex justify-center mb-2">
                <Button size="sm" variant="outline" onClick={goToday} className="text-xs" data-testid="today-btn">Hoy</Button>
              </div>

              {/* Week grid */}
              <div className="space-y-1">
                {weekData.map((day, i) => {
                  const isToday = day.date === today;
                  const isSelected = day.date === selectedDate;
                  const dateObj = new Date(day.date + 'T00:00:00');
                  const dayNum = dateObj.getDate();

                  return (
                    <button
                      key={day.date}
                      onClick={() => setSelectedDate(day.date)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all
                        ${isSelected ? 'bg-teal-600 text-white shadow-md' : isToday ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50'}
                      `}
                      data-testid={`day-${day.date}`}
                    >
                      <div className={`w-8 text-xs font-medium ${isSelected ? 'text-teal-200' : 'text-slate-400'}`}>
                        {DAY_NAMES_SHORT[i]}
                      </div>
                      <div className={`text-lg font-bold font-['Barlow_Condensed'] ${isSelected ? 'text-white' : 'text-slate-800'}`}>
                        {dayNum}
                      </div>
                      <div className="flex-1" />
                      {day.count > 0 && (
                        <div className="flex items-center gap-1.5">
                          {day.completed > 0 && (
                            <span className={`text-xs ${isSelected ? 'text-teal-200' : 'text-green-500'}`}>
                              {day.completed}/{day.count}
                            </span>
                          )}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                            ${isSelected ? 'bg-white/20 text-white' : day.completed === day.count ? 'bg-green-100 text-green-700' : 'bg-teal-100 text-teal-700'}
                          `}>
                            {day.count}
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Category legend */}
          <Card>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => {
                  const Icon = c.icon;
                  return (
                    <span key={c.value} className="flex items-center gap-1 text-xs text-slate-500">
                      <Icon size={12} className={c.color} /> {c.label}
                    </span>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: Day detail */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 font-['Barlow_Condensed'] capitalize" data-testid="selected-day-label">
              {selectedDayLabel}
            </h2>
            <Badge variant="outline" className="text-xs">
              {dayEvents.length} {dayEvents.length === 1 ? 'evento' : 'eventos'}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
          ) : dayEvents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarDays size={40} className="mx-auto mb-3 text-slate-200" />
                <p className="text-slate-400 text-sm">No hay eventos para este día</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={openNew} data-testid="empty-new-event-btn">
                  <Plus size={14} className="mr-1" /> Crear evento
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {dayEvents.map(ev => {
                const cat = getCatInfo(ev.category);
                const CatIcon = cat.icon;
                return (
                  <Card key={ev.id} className={`transition-all ${ev.completed ? 'opacity-50' : 'hover:shadow-md'}`} data-testid={`event-${ev.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        {/* Complete toggle */}
                        <button onClick={() => handleToggleComplete(ev.id)} className="mt-0.5 shrink-0" data-testid="toggle-complete-btn">
                          {ev.completed
                            ? <CheckCircle size={20} className="text-green-500" />
                            : <Circle size={20} className="text-slate-300 hover:text-teal-500 transition-colors" />
                          }
                        </button>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-teal-700 font-mono" data-testid="event-time">{ev.time}</span>
                            <CatIcon size={14} className={cat.color} />
                            <span className={`font-medium text-sm ${ev.completed ? 'line-through text-slate-400' : 'text-slate-800'}`} data-testid="event-title">
                              {ev.title}
                            </span>
                          </div>
                          {ev.description && (
                            <p className="text-xs text-slate-400 mt-0.5 ml-[52px]">{ev.description}</p>
                          )}
                          {ev.reminder && ev.reminder !== 'none' && (
                            <div className="ml-[52px] mt-1">
                              <Badge className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
                                <Bell size={8} className="mr-0.5" />
                                {REMINDERS.find(r => r.value === ev.reminder)?.label}
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid="edit-event-btn"><Edit size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setDeleteId(ev.id)} data-testid="delete-event-btn"><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">{editing ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ej: Revisar Orca" data-testid="event-title-input" />
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detalles..." rows={2} data-testid="event-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" />
              </div>
              <div>
                <Label>Hora</Label>
                <Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} data-testid="event-time-input" />
              </div>
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="event-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => {
                    const Icon = c.icon;
                    return <SelectItem key={c.value} value={c.value}><span className="flex items-center gap-1"><Icon size={12} className={c.color} /> {c.label}</span></SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Recordatorio</Label>
              <Select value={form.reminder} onValueChange={v => setForm({ ...form, reminder: v })}>
                <SelectTrigger data-testid="event-reminder-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REMINDERS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-event-btn">
              {saving ? 'Guardando...' : <><Save size={14} className="mr-1" /> Guardar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar evento</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-event">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
